import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Braces,
  Brain,
  Scissors,
  Search,
  Video,
  LogIn,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useParallax } from "@/hooks/useParallax";
import DocumentTitle from '@/components/DocumentTitle';

const Landing = () => {
  const { user } = useAuth();

  // Refs for parallax elements
  const heroRef = useRef<HTMLDivElement>(null);
  const starsRef = useRef<HTMLDivElement>(null);
  const planet1Ref = useRef<HTMLDivElement>(null);
  const planet2Ref = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  // Parallax styles
  const starsStyle = useParallax(starsRef, { speed: 0.05 });
  const planet1Style = useParallax(planet1Ref, { speed: -0.1 });
  const planet2Style = useParallax(planet2Ref, { speed: -0.15 });
  const featuresStyle = useParallax(featuresRef, { speed: 0.03 });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 text-white relative overflow-hidden">
      <DocumentTitle 
        title="VideoBrain.ai - Unlock the Hidden Value in Your Videos"
        description="VideoBrain.ai analyzes, indexes, and transforms your video content with advanced AI. Find exactly what you need in videos without watching hours of content."
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center py-4">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-pink-600 flex items-center justify-center relative">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">
              VideoBrain
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/features"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Features
            </Link>
            <Link
              to="/pricing"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/roadmap"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Roadmap
            </Link>
            <Link
              to="/about"
              className="text-gray-300 hover:text-white transition-colors"
            >
              About
            </Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button
                  asChild
                  variant="ghost"
                  className="text-white hover:bg-white/10"
                >
                  <Link to="/login" className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </Link>
                </Button>
                <Button asChild className="bg-pink-600 hover:bg-pink-700">
                  <Link to="/login?tab=register">Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Parallax background elements */}
      <div
        ref={starsRef}
        className="absolute inset-0 pointer-events-none"
        style={starsStyle}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>

        {/* Stars */}
        <div className="stars absolute inset-0">
          {[...Array(100)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${Math.random() * 5 + 3}s infinite ${
                  Math.random() * 5
                }s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Floating planet 1 */}
      <div
        ref={planet1Ref}
        className="absolute -right-32 top-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/20 to-indigo-600/20 blur-xl"
        style={planet1Style}
      ></div>

      {/* Floating planet 2 */}
      <div
        ref={planet2Ref}
        className="absolute -left-20 top-2/3 w-40 h-40 rounded-full bg-gradient-to-br from-pink-500/20 to-red-500/10 blur-lg"
        style={planet2Style}
      ></div>

      {/* Hero section */}
      <section
        ref={heroRef}
        className="py-20 flex-1 flex flex-col justify-center relative overflow-hidden"
      >
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300 animate-fade-in">
              Unlock the Hidden Value in Your Videos
            </h1>
            <p
              className="text-xl md:text-2xl text-blue-100 mb-12 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDelay: "0.2s" }}
            >
              VideoBrain analyzes, indexes, and transforms your video content
              with advanced AI, helping you find exactly what you need without
              watching hours of footage.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
              style={{ animationDelay: "0.4s" }}
            >
              <Button
                size="lg"
                asChild
                className="bg-pink-600 hover:bg-pink-700 px-8 py-6 text-lg group transition-all duration-300 hover:shadow-[0_0_15px_rgba(236,72,153,0.5)]"
              >
                <Link to="/login">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-pink-600 border-white/20 px-8 py-6 text-lg text-gray-50 hover:bg-white/10 backdrop-blur-sm"
              >
                <Link to="#">Watch Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section
        id="features"
        ref={featuresRef}
        className="py-20 bg-black/30 backdrop-blur-sm relative z-10"
        style={featuresStyle}
      >
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Powerful Video Intelligence
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature cards */}
            {[
              {
                icon: <Search className="h-6 w-6 text-video-blue" />,
                title: "Semantic Search",
                description:
                  "Find moments using natural language queries that understand concepts, not just keywords.",
              },
              {
                icon: <Brain className="h-6 w-6 text-video-blue" />,
                title: "AI-Powered Analysis",
                description:
                  "Automatically identify speakers, objects, and topics across your entire video library.",
              },
              {
                icon: <Scissors className="h-6 w-6 text-video-blue" />,
                title: "Smart Clip Generation",
                description:
                  "Create perfect clips with just a text description of what you're looking for.",
              },
              {
                icon: <Braces className="h-6 w-6 text-video-blue" />,
                title: "Developer API",
                description:
                  "Integrate video intelligence capabilities directly into your applications and workflows.",
              },
              {
                icon: <Video className="h-6 w-6 text-video-blue" />,
                title: "Interactive Transcripts",
                description:
                  "Navigate videos easily with synchronized, searchable transcripts and speaker identification.",
              },
              {
                icon: <ArrowRight className="h-6 w-6 text-video-blue" />,
                title: "Workflow Integration",
                description:
                  "Connect with your favorite editing tools and content management systems for seamless workflows.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-video-blue/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-12 w-12 rounded-lg bg-video-blue/20 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Video Workflow?
            </h2>

            <Button
              size="lg"
              asChild
              className="bg-pink-600 hover:bg-pink-700 px-8 py-6 text-lg group transition-all duration-300"
            >
              {user ? (
                <Link to="/dashboard" className="flex items-center">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              ) : (
                <Link to="/login" className="flex items-center">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-md py-12 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="rounded-md bg-pink-600 p-1">
                <div className="h-6 w-6 text-white flex items-center justify-center font-bold">
                  VB
                </div>
              </div>
              <span className="font-semibold text-xl">VideoBrain.ai</span>
            </div>
            <div className="flex flex-wrap gap-6">
              <Link
                to="/features"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/roadmap"
                className="text-gray-300 hover:text-white transition-colors"
              >
                Roadmap
              </Link>
              <Link
                to="/about"
                className="text-gray-300 hover:text-white transition-colors"
              >
                About
              </Link>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2025 VideoBrain.ai. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="#" className="text-gray-400 hover:text-white text-sm">
                Privacy Policy
              </Link>
              <Link to="#" className="text-gray-400 hover:text-white text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>
        {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}
      </style>
    </div>
  );
};

export default Landing;
